<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import AutoComplete from '@/js/components/autoComplete/AutoComplete.vue';
    import type { Option } from '@/js/components/autoComplete/types';
    import DateRangeInput from '@/js/components/DateRangeInput.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import {
        useQueryGetMigratedTenants,
        useQueryGetExternalSystemsForTenant,
        useMutationSyncAiInsightsData,
        useQueryGetSyncJobs,
    } from '@/js/services/api/backOffice/backOfficeQueries';
    import SyncJobsTable from '../components/SyncJobsTable.vue';

    const successDialogStore = useSuccessDialog();
    const { setFocus } = useElementFocus();

    const { data: tenantsData } = useQueryGetMigratedTenants();

    const selectedTenantId = ref<string | null>(null);
    const selectedExternalSystemId = ref<string | null>(null);
    const dateRange = ref<{ start?: string; end?: string }>({});

    const { data: externalSystemsData } = useQueryGetExternalSystemsForTenant(selectedTenantId);
    const { mutateAsync: syncMutation, isPending } = useMutationSyncAiInsightsData();

    const syncJobsTable = ref<InstanceType<typeof SyncJobsTable> | null>(null);

    const tenantSearch = ref<string | null>(null);
    const systemSearch = ref<string | null>(null);

    const tenantOptions = computed<Option[]>(() =>
        (tenantsData.value ?? []).map((t) => ({ name: t.name, value: t.id }))
    );

    const externalSystemOptions = computed<Option[]>(() =>
        (externalSystemsData.value ?? []).map((s) => ({ name: s.systemName, value: s.systemId }))
    );

    watch(selectedTenantId, () => {
        selectedExternalSystemId.value = null;
        systemSearch.value = null;
    });

    const onTenantSelect = (option: Option) => {
        selectedTenantId.value = option.value;
        setFocus('#back-office-sync-ai-insights-data-system-select');
    };

    const onTenantClear = () => {
        selectedTenantId.value = null;
    };

    const onSystemSelect = (option: Option) => {
        selectedExternalSystemId.value = option.value;
    };

    const onSystemClear = () => {
        selectedExternalSystemId.value = null;
    };

    const canSubmit = computed(
        () =>
            !!selectedTenantId.value &&
            !!selectedExternalSystemId.value &&
            !!dateRange.value.start &&
            !!dateRange.value.end
    );

    const submit = async () => {
        if (!canSubmit.value) return;

        const result = await syncMutation({
            tenantId: selectedTenantId.value!,
            externalSystemId: selectedExternalSystemId.value!,
            start: `${dateRange.value.start}T00:00:00`,
            end: `${dateRange.value.end}T23:59:59`,
        });

        successDialogStore.openDialog({
            message: __('back-office-sync-jobs-success', { id: result.id }),
        });
        syncJobsTable.value?.refetch();
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">
                {{ __('back-office-sync-ai-insights-data') }}
            </h1>
        </template>
        <template #content>
            <form class="w-lg max-w-full" @submit.prevent="submit">
                <FormGroup>
                    <AutoComplete
                        v-model="tenantSearch"
                        input-id="back-office-sync-ai-insights-data-tenant-select"
                        :label="__('back-office-sync-jobs-select-tenant')"
                        :options="tenantOptions"
                        @select="onTenantSelect"
                        @clear="onTenantClear"
                    />
                    <template v-if="selectedTenantId">
                        <AutoComplete
                            v-model="systemSearch"
                            input-id="back-office-sync-ai-insights-data-system-select"
                            :label="__('back-office-sync-jobs-select-system')"
                            :options="externalSystemOptions"
                            @select="onSystemSelect"
                            @clear="onSystemClear"
                        />
                    </template>
                    <div class="max-w-xs">
                        <DateRangeInput v-model="dateRange" />
                    </div>
                </FormGroup>

                <div class="mt-6">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!canSubmit"
                        :loading="isPending"
                    >
                        {{ __('back-office-sync-jobs-trigger') }}
                    </EchoButton>
                </div>
            </form>

            <SyncJobsTable
                ref="syncJobsTable"
                class="mt-10"
                :heading="__('back-office-sync-jobs-recent')"
                :use-jobs-query="() => useQueryGetSyncJobs('ImsSyncAsr')"
            />
        </template>
    </SettingsPageLayout>
</template>
